import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { Instructions } from "@p0security/shared/install/types";
import { AzureComponents } from "@p0security/shared/integrations/resources/azure/components";
import { azureAppComponentSetupFor } from "@p0security/shared/integrations/resources/azure/helper";
import { AzureIntegration } from "@p0security/shared/integrations/resources/azure/types";
import { Typography } from "antd";
import { CommandDisplay } from "components/Integrations/CommandDisplay";
import { FrontendInstallContext, FrontendInstaller } from "install/types";
import { ReactNode } from "react";

const clientIdFor = (context: FrontendInstallContext<AzureIntegration>) => {
  const { appName } = azureAppComponentSetupFor(context.environment);
  return `az ad app list --display-name "${appName}" --query "[0].appId" -o tsv`;
};

const appCreationCommandsFor = (
  context: FrontendInstallContext<AzureIntegration>
) => {
  const { config, environment } = context;
  const { serviceAccountId } = config.root[SINGLETON_ITEM];
  const { appName, credentialInfo } = azureAppComponentSetupFor(environment);
  const { audiences, issuer, name, description } = credentialInfo;
  return {
    shell: [
      {
        header: "Register your application with Azure",
        command: [
          `p0AppId=$(az ad app create --display-name "${appName}" --query appId -o tsv)`,
          `az ad sp create --id $p0AppId`,
        ].join("\n"),
      },
      {
        header: "Add federated identity credential for the app",
        command: [
          `json='${JSON.stringify({
            ...credentialInfo,
            subject: `${serviceAccountId}`,
          })}'`,
          `echo "$json" > credentials.json`,
          `az ad app federated-credential create --id $p0AppId --parameters credentials.json`,
        ].join("\n"),
      },
    ],
    iac: [
      {
        header:
          "Register your application with Azure and create a service principal",
        command: `resource "azuread_application_registration" "p0_azure_app" {
  display_name = "${appName}"
}
resource "azuread_service_principal" "p0_azure_app_sp" {
  depends_on                   = [azuread_application_registration.p0_azure_app]
  client_id                    = azuread_application_registration.p0_azure_app.client_id
}
`,
      },
      {
        header: " Add federated identity credential for the app",
        command: `resource "azuread_application_federated_identity_credential" "p0_azure_federated_identity" {
  depends_on     = [azuread_application_registration.p0_azure_app]
  application_id = azuread_application_registration.p0_azure_app.id
  display_name   = "${name}"
  description    = "${description}"
  audiences      = ["${audiences.join('", "')}"]
  issuer         = "${issuer}"
  subject        = "${serviceAccountId}"
}`,
      },
    ],
  };
};

const createAppInstructor = (
  context: FrontendInstallContext<AzureIntegration>,
  _id: string
): Instructions<ReactNode> => {
  const commands = appCreationCommandsFor(context);
  return {
    help: (
      <Typography.Paragraph>
        Run the following commands to create P0 App registration and federated
        credential using Azure cloud bash shell.
      </Typography.Paragraph>
    ),
    commands,
  };
};

export const azureAppInstaller: FrontendInstaller<
  typeof AzureComponents
>["azure-app"] = {
  instructions: createAppInstructor,
  items: {
    clientId: {
      labeler: (context) => {
        return <CommandDisplay commands={clientIdFor(context)} />;
      },
    },
  },
};
